<template>
  <ValidationObserver v-slot="{handleSubmit, invalid, reset}" ref="observer">
    <artisio-modal :visible="modal.show" :data="object" :title="computedTitle" :content-in-form="false"
                   :show-confirmation="showConfirmation" :is-loading="loading" scrollable size="lg"
                   modal-class="mobile-full-screen" @hidden="onHidden" @ok.prevent="handleSubmit(onSubmit)">
      <div class="row mb-2" v-if="computedSettlement">

        <div class="col-md-6">
          <div class="col-md-12">
            <span class="d-inline-block mr-3"><b>{{ $t('Settlement') }}</b></span>
            <a href="javascript:void(0)" @click="onSettlementClick(computedSettlement)">
              {{ computedSettlement.settlement_no }}
            </a>
          </div>

          <div class="col-md-12 mb-1" v-if="isUpdate">
            <span class="d-inline-block mr-3"><b>{{ $t('Debit Note') }}</b></span>
            <a href="javascript:void(0)" @click="onSettlementClick(object)">
              {{ object.settlement_no }}
            </a>
          </div>

          <div class="col-md-12 mb-2" v-if="computedSettlement.customer">
            <customer-email-address-info v-if="computedSettlement.customer"
                                         :customer="computedSettlement.customer"></customer-email-address-info>
          </div>
        </div>
        <div class="col-md-6">
          <input-widget :model="model" attribute="date" type="datepicker" :min="$getMonthAgo(model.created_at)"
                        :max="$getMonthCurrent(model.created_at)"
                        :rules="`min_date:${$getMonthAgo(model.created_at)}|max_date:${$getMonthCurrent(model.created_at)}|required`"/>
        </div>


        <div class="col-md-12">
          <table class="info-table table table-sm settlement-payments-table ">
            <tbody>
            <tr>
              <th class="border-top-0">{{ $t('Stock No') }}</th>
              <th class="border-top-0" v-if="isAuctionType">{{ $t('Lot No') }}</th>
              <th class="border-top-0">{{ $t('Title') }}</th>
              <th class="border-top-0">{{ $t('Total') }}</th>
            </tr>
            <tr v-if="computedSettlement.currency" v-for="financial_item in computedFinancialItems"
                :key="financial_item.uuid">
              <td>
                <router-link
                    v-if="$hasPermission('view_receipt')" target="_blank"
                    :to="{name: 'receipt.view', params: {id: getInventory(financial_item)['receipt_uuid']}}">
                  {{ getPartialStockNo(financial_item, 0) }}
                </router-link>
                <span v-else>{{ getPartialStockNo(financial_item, 0) }}</span>
                /
                <router-link
                    v-if="$hasPermission('view_inventoryitem')" target="_blank"
                    :to="{ name: 'inventory.view', params: { id: getInventory(financial_item)['uuid'] }}">
                  {{ getPartialStockNo(financial_item, 1) }}
                </router-link>
                <span v-else>{{ getPartialStockNo(financial_item, 1) }}</span>
              </td>
              <td v-if="isAuctionType">
                <router-link
                    v-if="$hasPermission('view_inventoryitem')" target="_blank"
                    :to="{ name: 'reservations.view', params: { id: financial_item.lot.reservation_uuid }}">
                  {{ financial_item.lot.lot_no }}
                </router-link>
                <span v-else>{{ financial_item.lot.lot_no }}</span>
              </td>
              <td v-html="getInventory(financial_item)['short_description']"/>
              <td>{{ getFinancialItemTotal(financial_item) | asCurrency(computedSettlement.currency.symbol) }}</td>
            </tr>
            <tr v-if="computedSettlement.currency">
              <td :colspan="computedColspan" class="text-right border-top-0 pb-0">
                <span class="d-inline-block mr-3"><b>{{ $t('Total Amount') }}</b></span>
                <span>
                  {{ computedSettlement.total_amount | asCurrency(computedSettlement.currency.symbol) }}
                </span>
              </td>
            </tr>
            </tbody>
          </table>
        </div>

      </div>
      <hr class="mt-2">
      <input-widget :model="model" attribute="comment" type="textarea"/>
      <template v-slot:modal-footer v-if="enableCreditNoteWithoutRefund && !isUpdate">
        <cancel-button @click="onHidden">
          {{ $t('Cancel') }}
        </cancel-button>
        <submit-button @click="onSubmit">
          {{ $t('Submit')}}
        </submit-button>
        <submit-button @click="onSubmitIssue">
          {{ $t('Submit and Issue New Settlement')}}
        </submit-button>
      </template>
    </artisio-modal>
  </ValidationObserver>
</template>

<script>
  import DebitNoteModel from "./DebitNote.model.js";
  import InputWidget from "../../core/components/input-widget/InputWidget";
  import ArtisioModal from "../../core/components/artisio-modal/ArtisioModal";
  import {createNamespacedHelpers} from 'vuex';
  import {
    SETTLEMENT_TYPE_AUCTION,
    SETTLEMENT_TYPE_AUCTION_INTRODUCTORY,
    SETTLEMENT_TYPE_DEBIT_NOTE,
    SETTLEMENT_TYPE_MANUAL,
    SETTLEMENT_TYPE_MANUAL_INTRODUCTORY,
    SOURCE_SETTLEMENTS_TABLE
  } from "@/modules/constants";
  import CancelButton from "@/core/components/common/CancelButton.vue";
  import SubmitButton from "@/core/components/common/SubmitButton.vue";
  import _ from 'lodash';
  import CustomerEmailAddressInfo from "@/core/components/customer-info/CustomerEmailAddressInfo";
  import SettlementService from "@/modules/settlements/SettlementService";

  const {mapState, mapActions} = createNamespacedHelpers('settlements');
  const {mapState: globalMapState} = createNamespacedHelpers('global');

  export default {
    name: "DebitNoteModal",
    components: {InputWidget, ArtisioModal, CancelButton, SubmitButton, CustomerEmailAddressInfo},
    data() {
      return {
        loading: false,
        showConfirmation: true,
        model: new DebitNoteModel(),
      }
    },
    computed: {
      ...mapState({
        modal: state => state.debitNoteModal,
        object: state => state.debitNoteModal.object,
      }),
      ...globalMapState({
        enableCreditNoteWithoutRefund: state => state.enableCreditNoteWithoutRefund
      }),
      computedTitle() {
        if (!this.model || !this.model.uuid) {
          return this.$i18n.t('Create Debit Note')
        }
        return this.$i18n.t('Update Debit Note {no}', {no: this.model.settlement_no})
      },
      computedSettlement() {
        return this.isUpdate ? this.object.debited_settlement : this.object;
      },
      isUpdate() {
        return this.object && this.object.type === SETTLEMENT_TYPE_DEBIT_NOTE;
      },
      isAuctionType() {
        return this.computedSettlement.type === SETTLEMENT_TYPE_AUCTION || this.computedSettlement.type === SETTLEMENT_TYPE_AUCTION_INTRODUCTORY;
      },
      isManualType() {
        return this.computedSettlement.type === SETTLEMENT_TYPE_MANUAL || this.computedSettlement.type === SETTLEMENT_TYPE_MANUAL_INTRODUCTORY;
      },
      computedFinancialItems() {
        let financialItems, fiDataSource;

        if (this.isUpdate) {
          fiDataSource = this.computedSettlement.data;
        } else {
          fiDataSource = this.computedSettlement;
        }

        if (this.computedSettlement.type === SETTLEMENT_TYPE_AUCTION_INTRODUCTORY || this.computedSettlement.type === SETTLEMENT_TYPE_MANUAL_INTRODUCTORY) {
          financialItems = fiDataSource.introductory_financial_items;
        } else {
          financialItems = fiDataSource.financial_items;
        }

        return financialItems;
      },
      computedColspan() {
        return this.isAuctionType ? 4 : 3;
      }
    },
    watch: {
      object() {
        if (this.isUpdate) {
          this.model = new DebitNoteModel(this.object);
        }
      },
    },
    methods: {
      ...mapActions([
        'hideDebitNoteModal',
        'createDebitNote',
        'updateDebitNote',
        'showModal',
        'getModalData',
        'showManualSettlementModal',
        'getManualSettlementModalData',
        'showIntroductorySettlementModal',
        'getIntroductorySettlementModalData'
      ]),
      onHidden() {
        this.model = new DebitNoteModel();
        this.hideDebitNoteModal();
      },
      async onSubmit() {
        if (this.model.is_not_editable) {
          this.$errorDialog(this.$i18n.t('You can not edit legacy settlement'));
          return;
        }
        this.loading = true;
        let params = {
          settlement_uuid: this.object.uuid,
          comment: this.model.comment,
          date: this.model.date
        };
        let response;
        if (this.isUpdate) {
          response = await this.updateDebitNote(params);
        } else {
          response = await this.createDebitNote(params);
        }
        this.loading = false;
        if (response.success) {
          this.showConfirmation = false;
          this.onHidden();
        } else {
          if (response.body.non_field_errors) {
            this.$errorDialog(response.body.non_field_errors[0]);
          }
        }
      },
      async onSubmitIssue() {
        let success = await this.$confirm(
            null,
            null,
            null,
            null,
            this.$i18n.t('Please Confirm'),
            this.$i18n.t('Yes'),
            this.$i18n.t('No'),
            null,
            {
              label: this.$i18n.t("Please type 'confirm' to proceed"),
              text: 'confirm'
            }
        )
        if (!success) {
          return;
        }

        let params = {
          customer_uuid: this.object.customer.uuid,
        };

        let debitNoteParams = {
          comment: this.model.comment,
          date: this.model.date
        };

        if (this.object.type === SETTLEMENT_TYPE_AUCTION) {
          params.auction_uuid = this.object.auction_uuid;
          this.showModal({source: SOURCE_SETTLEMENTS_TABLE, debitNoteWithoutRefundParams: debitNoteParams})

          await this.getModalData({
            params,
            object: this.object,
            sale: this.object.auction,
            customer: this.object.customer,
            removeOldData: true
          });
        } else if (this.object.type === SETTLEMENT_TYPE_MANUAL) {
          params.is_manual = true;

          this.showManualSettlementModal({source: SOURCE_SETTLEMENTS_TABLE, debitNoteWithoutRefundParams: debitNoteParams})
          await this.getManualSettlementModalData({
            params,
            object: this.object,
            customer: this.object.customer,
            removeOldData: true
          });
        } else if (this.object.type === SETTLEMENT_TYPE_AUCTION_INTRODUCTORY || this.object.type === SETTLEMENT_TYPE_MANUAL_INTRODUCTORY) {
          this.showIntroductorySettlementModal(
            {
              source: SOURCE_SETTLEMENTS_TABLE,
              customer: this.object.customer,
              sale: _.cloneDeep(this.object.auction),
              type: this.object.type,
              debitNoteWithoutRefundParams: debitNoteParams
            }
          )
          this.getIntroductorySettlementModalData({object: this.object, removeOldData: true})
        }
        this.onHidden();
      },
      async onSettlementClick(settlement) {
        await SettlementService.viewSettlementPdf(this, settlement)
      },
      getInventory(item) {
        if (this.isUpdate || this.isManualType) {
          return item.inventory;
        } else {
          return item.lot.inventory;
        }
      },
      getPartialStockNo(item, index) {
        const inventory = this.getInventory(item);
        return inventory['stock_no'].split('/')[index];
      },
      getFinancialItemTotal(item) {
        let total;
        if (this.computedSettlement.type === SETTLEMENT_TYPE_AUCTION || this.computedSettlement.type === SETTLEMENT_TYPE_MANUAL) {
          total = item.vendor_total;
        } else {
          total = item.introductory_commission + item.introductory_commission_vat;
        }

        return total;
      }
    }
  }
</script>

<style scoped>

</style>
